import React, { useState, useEffect, useContext } from 'react'
import { Typography, Row, Col, Pagination, Switch, message, Tooltip, Tag, Modal, Input } from 'antd'
import {
  PlusOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  EditOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import Table from '../../components/common/table/table'
import Button from '../../components/common/button/button'
import Spinner from '../../components/common/spinner/spinner'
import VendorForm from './createVendorForm/createVendorForm'
import VendorAttachment from './vendorAttachments/vendorAttachment'
import { fetchVendorList, toggleVendorStatus,exportVendorExcel} from '../../api/vendorApiService/vendorApiUtils'
import { useMsal } from '@azure/msal-react'
import { UserContext } from '../../context/userContext'
import './vendor.scss'

const { Title } = Typography

const Vendor = () => {
  const { instance: msalInstance } = useMsal()

  // Get vendor permissions
  const { getUserPermissions } = useContext(UserContext)
  const vendorPermissions = getUserPermissions('vendor')
  const canWriteVendor = vendorPermissions?.write === true

  // Loading states
  const [loading, setLoading] = useState(true)
  const [statusLoadingVendors, setStatusLoadingVendors] = useState(new Set())
  const [refreshingAfterCreate, setRefreshingAfterCreate] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  // Data
  const [vendors, setVendors] = useState([])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalVendors, setTotalVendors] = useState(0)

  // Modals
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [editingVendor, setEditingVendor] = useState(null)
  const [isAttachmentModalVisible, setIsAttachmentModalVisible] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [attachmentType, setAttachmentType] = useState('general')
  const [pendingModalReopen, setPendingModalReopen] = useState(null)

  // Search state
  const [isSearchActive, setIsSearchActive] = useState(false)

  // Table height
  const [tableScrollY, setTableScrollY] = useState(600)

  useEffect(() => {
    const updateTableHeight = () => {
      const headerHeight = 180
      const footerHeight = 310
      const newHeight = window.innerHeight - headerHeight - footerHeight
      setTableScrollY(newHeight > 300 ? newHeight : 300)
    }
    updateTableHeight()
    window.addEventListener('resize', updateTableHeight)
    return () => window.removeEventListener('resize', updateTableHeight)
  }, [])

  const normalizeStatus = (status) => {
    if (typeof status === 'boolean') return status
    if (typeof status === 'string') return status.toLowerCase() === 'true' || status === '1'
    if (typeof status === 'number') return status === 1
    return Boolean(status)
  }

  const normalizeAttachments = (attachments) => {
    if (!attachments) return []
    if (Array.isArray(attachments)) return attachments
    return [attachments]
  }

const exportVendorsToCSV = async () => {
  const hide = message.loading('Preparing Excel download...', 0)
  try {
    setExportLoading(true)
    const blob = await exportVendorExcel(msalInstance)

    const date = new Date()
    const filename = `Vendors_List_${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}.xlsx`

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    message.success('Vendor list downloaded successfully')
  } catch (error) {
    console.error('Error downloading Vendor Excel:', error)
    message.error('Failed to download vendor list')
  } finally {
    setExportLoading(false)
    hide()
  }
}

  const loadVendors = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true)
      const response = await fetchVendorList(msalInstance, { page, limit })

      let normalizedVendors = []
      let total = 0

      if (response && response.data) {
        normalizedVendors = response.data.map(v => ({
          ...v,
          status: normalizeStatus(v.active ?? v.status),
          birAttachment: normalizeAttachments(v.birAttachment),
          additionalAttachments: normalizeAttachments(v.additionalAttachments)
        }))
        total = response.pagination?.total ?? response.total ?? normalizedVendors.length
      } else if (Array.isArray(response)) {
        normalizedVendors = response.map(v => ({
          ...v,
          status: normalizeStatus(v.active ?? v.status),
          birAttachment: normalizeAttachments(v.birAttachment),
          additionalAttachments: normalizeAttachments(v.additionalAttachments)
        }))
        total = normalizedVendors.length
      }

      setVendors(normalizedVendors)
      setTotalVendors(total)
    } catch (error) {
      console.error('Error loading vendors:', error)
      message.error('Failed to load vendors.')
      setVendors([])
      setTotalVendors(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadVendors()
  }, [])

  useEffect(() => {
    if (pendingModalReopen && vendors.length > 0) {
      const { vendorId, attachmentType: type } = pendingModalReopen
      const updatedVendor = vendors.find(v => v._id === vendorId)
      if (updatedVendor) {
        setTimeout(() => {
          setSelectedVendor(updatedVendor)
          setAttachmentType(type)
          setIsAttachmentModalVisible(true)
          setPendingModalReopen(null)
        }, 2000)
      }
    }
  }, [vendors, pendingModalReopen])

  const handleStatusToggle = async (vendorId, newStatus, vendorName) => {
    Modal.confirm({
      title: 'Confirm Status Change',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            Are you sure you want to change the status of <strong>{vendorName}</strong> to{' '}
            <strong>{newStatus ? 'Active' : 'Inactive'}</strong>?
          </p>
        </div>
      ),
      okText: 'Confirm',
      okButtonProps: {
        className: newStatus ? 'app-button app-button-primary' : 'app-button',
        danger: !newStatus,
        style: newStatus ? {} : { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }
      },
      cancelText: 'Cancel',
      cancelButtonProps: {
        className: 'app-button app-button-default'
      },
      onOk: async () => {
        try {
          setStatusLoadingVendors(prev => new Set(prev).add(vendorId))

          setVendors(prev => prev.map(v =>
            (v.vendorID === vendorId || v.id === vendorId) ? { ...v, status: newStatus } : v
          ))

          const response = await toggleVendorStatus(vendorId, newStatus, msalInstance)
          message.success(response.message || `Vendor ${newStatus ? 'activated' : 'deactivated'} successfully`)
          await loadVendors(currentPage, pageSize)
        } catch (error) {
          console.error('Error updating status:', error)
          setVendors(prev => prev.map(v =>
            (v.vendorID === vendorId || v.id === vendorId) ? { ...v, status: !newStatus } : v
          ))
          message.error(error.response?.data?.message || 'Failed to update status.')
          await loadVendors(currentPage, pageSize)
        } finally {
          setStatusLoadingVendors(prev => {
            const newSet = new Set(prev)
            newSet.delete(vendorId)
            return newSet
          })
        }
      },
      onCancel: () => {}
    })
  }

  const handleAttachmentClick = (vendor, type) => {
    setSelectedVendor(vendor)
    setAttachmentType(type)
    setIsAttachmentModalVisible(true)
  }

  const handleAttachmentModalClose = () => {
    setIsAttachmentModalVisible(false)
    setSelectedVendor(null)
  }

  const handleAttachmentsUpdate = (updatedAttachments, type) => {
    setVendors(prev => prev.map(v =>
      v._id === selectedVendor?._id
        ? { ...v, [type === 'bir' ? 'birAttachment' : 'additionalAttachments']: updatedAttachments }
        : v
    ))
    setSelectedVendor(prev => ({
      ...prev,
      [type === 'bir' ? 'birAttachment' : 'additionalAttachments']: updatedAttachments
    }))
  }

  const handleUploadSuccess = async (uploadedVendorId, uploadedAttachmentType) => {
    setIsAttachmentModalVisible(false)
    setSelectedVendor(null)
    setPendingModalReopen({ vendorId: uploadedVendorId, attachmentType: uploadedAttachmentType })
    try {
      await loadVendors(currentPage, pageSize)
    } catch {
      message.error('Failed to reload vendor data')
      setPendingModalReopen(null)
    }
  }

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor)
    setIsFormVisible(true)
  }

  const handleAddVendor = () => {
    setEditingVendor(null)
    setIsFormVisible(true)
  }

  const handleFormCancel = () => {
    setIsFormVisible(false)
    setEditingVendor(null)
  }

  const handleFormSubmit = async () => {
    try {
      setRefreshingAfterCreate(true)
      await loadVendors(currentPage, pageSize)
      setIsFormVisible(false)
      setEditingVendor(null)
    } catch (error) {
      console.error('Error after form submit:', error)
    } finally {
      setRefreshingAfterCreate(false)
    }
  }

  const handlePageChange = (page, size) => {
    setCurrentPage(page)
    if (size !== pageSize) setPageSize(size)
  }

  const handlePageSizeChange = (current, size) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  /**
   * Generic column search handler for both vendorID and vendorName columns.
   * Calls the searchVendor API, then client-side filters by the given field
   * so each column's dropdown only surfaces results relevant to that field.
   *
   * @param {string} searchTerm - The value typed in the filter input
   * @param {string} field      - The vendor object key to filter on ('vendorID' | 'vendorName')
   */
  const handleColumnSearch = async (searchTerm, field) => {
    if (!searchTerm?.trim()) {
      setIsSearchActive(false)
      setCurrentPage(1)
      await loadVendors(1, pageSize)
      return
    }

    try {
      setLoading(true)
      setIsSearchActive(true)

      const { searchVendor } = require('../../api/vendorApiService/vendorApiUtils')
      const response = await searchVendor(msalInstance, searchTerm.trim())

      if (response?.data) {
        const normalized = response.data.map(v => ({
          ...v,
          status: normalizeStatus(v.active ?? v.status),
          birAttachment: normalizeAttachments(v.birAttachment),
          additionalAttachments: normalizeAttachments(v.additionalAttachments)
        }))

        // Client-side refinement: only show rows that match the searched field
        const filtered = normalized.filter(v =>
          (v[field] || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
        )

        setVendors(filtered)
        setTotalVendors(filtered.length)
      } else {
        setVendors([])
        setTotalVendors(0)
      }
    } catch (error) {
      console.error('Search error:', error)
      message.error('Failed to search vendor.')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Reusable filter dropdown builder used by both Vendor ID and Vendor Name columns.
   *
   * @param {string} placeholder - Input placeholder text
   * @param {string} field       - Vendor field key to search on
   */
  const buildFilterDropdown = (placeholder, field) =>
    ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm()
            handleColumnSearch(selectedKeys[0], field)
          }}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="primary"
            onClick={() => {
              confirm()
              handleColumnSearch(selectedKeys[0], field)
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ flex: 1, backgroundColor: '#ad0270', borderColor: '#ad0270' }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters()
              confirm()
              handleColumnSearch('', field)
            }}
            size="small"
            style={{ flex: 1 }}
          >
            Reset
          </Button>
        </div>
      </div>
    )

  const getCurrentAttachments = () => {
    if (!selectedVendor) return []
    return attachmentType === 'bir'
      ? selectedVendor.birAttachment || []
      : selectedVendor.additionalAttachments || []
  }

  const columns = [
    {
      title: 'Sr. No',
      key: 'srNo',
      width: 80,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: 'Vendor Id',
      dataIndex: 'vendorID',
      key: 'vendorID',
      width: 180,
      filterDropdown: buildFilterDropdown('Search Vendor ID', 'vendorID'),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#ad0270' : undefined }} />
      )
    },
    {
      title: 'Vendor Name',
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 250,
      filterDropdown: buildFilterDropdown('Search Vendor Name', 'vendorName'),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#ad0270' : undefined }} />
      ),
      render: text => text && text.length > 20 ? `${text.substring(0, 20)}...` : (text || '-')
    },
    {
      title: 'Vendor Address',
      dataIndex: 'vendorAddress',
      key: 'vendorAddress',
      width: 300,
      ellipsis: true
    },
    {
      title: 'TIN No',
      dataIndex: 'tinNumber',
      key: 'tinNumber',
      width: 150
    },
    {
      title: 'SAP Reference ID',
      dataIndex: 'sapReferenceId',
      key: 'sapReferenceId',
      width: 180,
      ellipsis: true
    },
    {
      title: 'BIR Certificate',
      key: 'birAttachment',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const has = record.birAttachment?.length > 0
        return (
          <FileTextOutlined
            style={{ color: has ? '#000' : '#d9d9d9', fontSize: '18px', cursor: 'pointer' }}
            onClick={() => handleAttachmentClick(record, 'bir')}
            title={has ? `${record.birAttachment.length} BIR attachment(s)` : 'No BIR attachments'}
          />
        )
      }
    },
    {
      title: 'Additional Attachments',
      key: 'additionalAttachments',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const has = record.additionalAttachments?.length > 0
        return (
          <PaperClipOutlined
            style={{ color: has ? '#1890ff' : '#d9d9d9', fontSize: '18px', cursor: 'pointer' }}
            onClick={() => handleAttachmentClick(record, 'additional')}
            title={has ? `${record.additionalAttachments.length} additional attachment(s)` : 'No additional attachments'}
          />
        )
      }
    },
    {
      title: 'Vendor Status',
      key: 'status',
      width: 180,
      filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
      onFilter: (value, record) => record.status === value,
      render: (_, record) => {
        const vendorId = record.vendorID || record.id
        const isLoading = statusLoadingVendors.has(vendorId)
        const status = normalizeStatus(record.status)

        if (canWriteVendor) {
          return (
            <div className="vendor-status-toggle">
              <Switch
                checked={status}
                onChange={(checked) => handleStatusToggle(vendorId, checked, record.vendorName)}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                className="vendor-toggle-switch"
                loading={isLoading}
                disabled={isLoading}
              />
            </div>
          )
        }

        return (
          <Tag color={status ? 'green' : 'red'} style={{ width: 80, textAlign: 'center' }}>
            {status ? 'Active' : 'Inactive'}
          </Tag>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={!canWriteVendor ? 'You do not have permission to edit vendors' : 'Edit Vendor'}>
          <span>
            <EditOutlined
              style={{
                color: canWriteVendor ? '#1890ff' : '#d9d9d9',
                fontSize: '18px',
                cursor: canWriteVendor ? 'pointer' : 'not-allowed'
              }}
              onClick={() => canWriteVendor && handleEditVendor(record)}
            />
          </span>
        </Tooltip>
      )
    }
  ]

  return (
    <div className="vendor-page">
      <Row className="vendor-page__header">
        <Col span={24}>
          <Title level={4} className="vendor-page__title">Vendor Management</Title>
          <hr />
        </Col>
      </Row>

      <Row className="vendor-page__actions" justify="end">
        <Button
          type="default"
          icon={<DownloadOutlined />}
          onClick={exportVendorsToCSV}
          loading={exportLoading}
          disabled={exportLoading || loading || vendors.length === 0}
          style={{ marginRight: '8px' }}
        >
          Download
        </Button>

        <Tooltip title={!canWriteVendor ? 'You do not have permission to add new vendors' : ''}>
          <span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddVendor}
              disabled={!canWriteVendor}
            >
              Add New Vendor
            </Button>
          </span>
        </Tooltip>
      </Row>

      <div className="vendor-page__content">
        <Table
          columns={columns}
          dataSource={(loading || refreshingAfterCreate) ? [] : vendors.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
          rowKey={record => record._id || record.vendorID || record.id}
          scroll={{ x: 1440, y: tableScrollY }}
          sticky
          pagination={false}
          locale={{
            emptyText: loading || refreshingAfterCreate
              ? (
                <div>
                  <Spinner size="large" />
                  <div style={{ marginTop: 8, color: '#666' }}>
                    {refreshingAfterCreate ? 'Refreshing...' : 'Loading vendors...'}
                  </div>
                </div>
              )
              : 'No vendors found'
          }}
        />

        {!loading && !refreshingAfterCreate && totalVendors > 0 && !isSearchActive && (
          <div className="vendor-page__pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalVendors}
              showSizeChanger
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} vendors`}
              pageSizeOptions={['5', '10', '20', '50']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      <VendorForm
        visible={isFormVisible}
        onCancel={handleFormCancel}
        onSubmit={handleFormSubmit}
        editingVendor={editingVendor}
      />

      <VendorAttachment
        visible={isAttachmentModalVisible}
        onClose={handleAttachmentModalClose}
        vendorName={selectedVendor?.vendorName}
        vendorId={selectedVendor?._id}
        attachments={getCurrentAttachments()}
        attachmentType={attachmentType}
        onAttachmentsUpdate={handleAttachmentsUpdate}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  )
}

export default Vendor